import React, { useState, useEffect } from "react";
import OverlayModal from "./components/OverlayModal";
import ChatScreen from "./components/ChatScreen";
import { handleQuickButton, quickButtons } from "./utils/buttonHandlers";
import ContactCard from "./components/ContactCard";
import SkillsCard from "./components/SkillsCard";
import WorkExperienceCard from "./components/WorkExperienceCard";
import ProjectsCard from "./components/ProjectCard";
import Markdown from "markdown-to-jsx";
import "./App.css";

function App() {
  const [showOverlay, setShowOverlay] = useState(false);
  const [showChat, setShowChat] = useState(false);
  const [messages, setMessages] = useState([]);
  const [showContentModal, setShowContentModal] = useState(false);
  const [currentContent, setCurrentContent] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [currentVideoSrc, setCurrentVideoSrc] = useState("/avatar_active.mp4");

  // Handle smooth video transitions
  useEffect(() => {
    const newVideoSrc = isLoading ? "/avatar_thinks.mp4" : "/avatar_active.mp4";
    if (newVideoSrc !== currentVideoSrc) {
      setCurrentVideoSrc(newVideoSrc);
    }
  }, [isLoading, currentVideoSrc]);

  const handleButtonClick = async (label) => {
    if (label === "Contact") {
      setCurrentContent({ type: "contact_card" });
      setShowContentModal(true);
    } else if (label === "Skills") {
      setCurrentContent({ type: "skills_card" });
      setShowContentModal(true);
    } else if (label === "Experience") {
      setCurrentContent({ type: "work_card" });
      setShowContentModal(true);
    } else if (label === "Projects") {
      setCurrentContent({ type: "projects_card" });
      setShowContentModal(true);
    } else if (label === "Me") {
      const prompt =
        "Introduce Vibhor professionally in detail in presentable response format.";
      setIsLoading(true);
      try {
        const res = await fetch(
          "https://ai-portfolio-backend-w6r0.onrender.com/chat",
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ message: prompt }),
          }
        );
        const data = await res.json();
        setCurrentContent({ type: "bot", text: data.response });
        setShowContentModal(true);
      } catch {
        setCurrentContent({ type: "bot", text: "Error: Server unreachable." });
        setShowContentModal(true);
      }
      setIsLoading(false);
    }
  };

  return (
    <div
      style={{
        fontFamily: "'Inter', sans-serif",
        background: "linear-gradient(180deg, #fff 60%, #f7f9fb 100%)",
        minHeight: "100vh",
        paddingTop: "15px",
        position: "relative",
      }}
    >
      {/* Watermark */}
      <div
        style={{
          position: "fixed",
          left: 0,
          bottom: 0,
          width: "100vw",
          zIndex: 0,
          pointerEvents: "none",
          userSelect: "none",
          overflow: "hidden",
          display: "flex",
          justifyContent: "center",
        }}
      >
        <span
          style={{
            fontWeight: 900,
            letterSpacing: "0px",
            opacity: 1,
            lineHeight: 1,
            whiteSpace: "nowrap",
            textShadow: "0px 8px 28px rgba(80,80,120,0.18), 0px 1px 0px #fff",
            WebkitMaskImage:
              "linear-gradient(to bottom, transparent 0%, #000 250%, #000 100%)",
            maskImage:
              "linear-gradient(to bottom, transparent 0%, #000 150%, #000 100%)",
            fontSize: "clamp(60px, 20vw, 180px)",
            color: "#f8f9fb",
          }}
        >
          Vibhor
        </span>
      </div>

      {/* Floating Badge */}
      <div
        className="floating-badge"
        style={{
          position: "fixed",
          top: "20px",
          left: "20px",
          background: "white",
          borderRadius: "24px",
          boxShadow: "0 2px 10px rgba(6,6,6,0.06)",
          display: "flex",
          alignItems: "center",
          padding: "6px 18px",
          zIndex: 20,
        }}
      >
        <span
          style={{
            display: "inline-block",
            width: "10px",
            height: "10px",
            borderRadius: "50%",
            background: "#08C972",
            marginRight: "8px",
          }}
        />
        <span style={{ fontSize: "14px", color: "#222" }}>need an intern?</span>
      </div>

      {/* Info Button - only visible on home screen */}
      {!showChat && (
        <button
          onClick={() => setShowOverlay(true)}
          style={{
            position: "fixed",
            top: "clamp(15px, 4vw, 25px)",
            right: "clamp(15px, 4vw, 25px)",
            background: "#111",
            color: "#fff",
            border: "none",
            borderRadius: "50%",
            width: "clamp(28px, 6vw, 32px)",
            height: "clamp(28px, 6vw, 32px)",
            fontSize: "clamp(16px, 4vw, 20px)",
            fontFamily: "'Verdana', 'Geneva', sans-serif",
            fontWeight: "bold",
            lineHeight: "clamp(28px, 6vw, 32px)",
            cursor: "pointer",
            boxShadow: "0 1px 6px rgba(0,0,0,0.08)",
            zIndex: 20,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
          aria-label="Information"
        >
          i
        </button>
      )}

      {/* Main job: toggle screen between home and chat */}
      {showChat ? (
        <ChatScreen
          onBack={() => setShowChat(false)}
          isLoading={isLoading}
          setIsLoading={setIsLoading}
        />
      ) : (
        <>
          {/* Home screen header */}
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              marginTop: "36px",
            }}
          >
            <span
              style={{
                fontSize: "19px",
                fontWeight: 500,
                color: "#242424",
                marginBottom: "7px",
              }}
            >
              Hey, I'm Vibhor{" "}
              <span role="img" aria-label="wave">
                👋
              </span>
            </span>
            <h1
              onClick={() => setShowOverlay(true)}
              className="header-title"
              style={{
                fontWeight: "bold",
                margin: 0,
                marginBottom: "32px",
                cursor: "pointer",
              }}
            >
              <span style={{ color: "#2584FF" }}>AI </span>
              <span style={{ color: "#8F39FF" }}>Portfolio</span>
            </h1>
          </div>

          {/* Avatar */}
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              marginBottom: "28px",
            }}
          >
            <video
              src={currentVideoSrc}
              autoPlay
              loop
              muted
              preload="auto"
              className="avatar-image"
              style={{
                height: currentVideoSrc === "/avatar_thinks.mp4" ? 250 : "auto",
                borderRadius: "30px",
                objectFit: "cover",
                background: "#f4f5f7",
                width: currentVideoSrc === "/avatar_thinks.mp4" ? 250 : 190,
                transition: "width 0.3s ease, height 0.3s ease",
              }}
            />
          </div>

          {/* Start Chatting Button */}
          <div
            style={{ display: "flex", justifyContent: "center", marginTop: 0 }}
          >
            <button
              onClick={() => setShowChat(true)}
              className="start-chatting-button"
              style={{
                padding: "15px 30px",
                fontSize: "15px",
                fontWeight: "bold",
                borderRadius: "30px",
                cursor: "pointer",
                background: "linear-gradient(90deg,#5C7CFA,#9775FA)",
                color: "white",
                border: "none",
                boxShadow: "0 4px 15px rgba(92,124,250,0.45)",
              }}
            >
              Start Chatting
            </button>
          </div>

          {/* Navigation Buttons */}
          <div
            className="quick-buttons-container"
            style={{
              display: "flex",
              justifyContent: "center",
              gap: "26px",
              margin: "18px auto",
              flexWrap: "wrap",
            }}
          >
            {quickButtons.map((item) => (
              <div
                key={item.label}
                className="quick-button"
                onClick={() => handleButtonClick(item.label)}
                style={{
                  width: "110px",
                  height: "88px",
                  background: "white",
                  borderRadius: "22px",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  boxShadow: "0 2px 12px 0 rgba(0,0,0,0.045)",
                  cursor: "pointer",
                  border: "1.5px solid #F5F6F8",
                  transition: "all 0.2s ease",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = "translateY(-2px)";
                  e.currentTarget.style.boxShadow =
                    "0 4px 20px 0 rgba(0,0,0,0.1)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = "translateY(0)";
                  e.currentTarget.style.boxShadow =
                    "0 2px 12px 0 rgba(0,0,0,0.045)";
                }}
              >
                <span style={{ fontSize: 30 }}>{item.icon}</span>
                <span
                  style={{
                    fontSize: "14px",
                    color: "#464646",
                    marginTop: "8px",
                    fontWeight: 500,
                  }}
                >
                  {item.label}
                </span>
              </div>
            ))}
          </div>
        </>
      )}

      {/* Overlay Modal */}
      {showOverlay && (
        <OverlayModal
          onClose={() => setShowOverlay(false)}
          onStartChat={() => {
            setShowOverlay(false);
            setShowChat(true);
          }}
          onContact={() => {
            setShowOverlay(false);
            setCurrentContent({ type: "contact_card" });
            setShowContentModal(true);
          }}
        />
      )}

      {/* Content Modal */}
      {showContentModal && currentContent && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(0, 0, 0, 0.5)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 1000,
            padding: "20px",
          }}
          onClick={() => setShowContentModal(false)}
        >
          <div
            style={{
              backgroundColor: "white",
              borderRadius: "20px",
              maxWidth: "90vw",
              maxHeight: "90vh",
              overflow: "auto",
              position: "relative",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Close button */}
            <button
              onClick={() => setShowContentModal(false)}
              style={{
                position: "absolute",
                top: "28px",
                right: "28px",
                background: "#111",
                color: "#fff",
                border: "none",
                borderRadius: "50%",
                width: "30px",
                height: "30px",
                fontSize: "24px",
                lineHeight: "30px",
                cursor: "pointer",
                boxShadow: "0 1px 6px rgba(0,0,0,0.08)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                zIndex: 1001,
              }}
            >
              ×
            </button>

            {/* Content */}
            <div style={{ padding: "20px" }}>
              {currentContent.type === "contact_card" ? (
                <ContactCard />
              ) : currentContent.type === "skills_card" ? (
                <SkillsCard />
              ) : currentContent.type === "work_card" ? (
                <WorkExperienceCard />
              ) : currentContent.type === "projects_card" ? (
                <ProjectsCard />
              ) : currentContent.type === "bot" ? (
                <div
                  style={{
                    background: "#f6f7fa",
                    color: "#222",
                    borderRadius: "14px",
                    padding: "20px",
                    maxWidth: "600px",
                    fontSize: "14px",
                    lineHeight: "1.6",
                  }}
                >
                  <Markdown
                    options={{
                      forceBlock: true,
                      overrides: {
                        a: {
                          component: (props) => (
                            <a {...props} target="_blank" rel="noreferrer" />
                          ),
                        },
                      },
                    }}
                  >
                    {currentContent.text}
                  </Markdown>
                </div>
              ) : null}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default App;
